import { Banner } from '@/common/banner/Banner';
import { ExportButton } from '@/common/buttons/ExportButton';
import { Grid } from '@/common/grid/Grid';
import { useModuleFromRoute } from '@/common/hooks/route/useModuleFromRoute';
import { useNavState } from '@/common/navState/NavStateContext';
import { useNotif } from '@/common/notif/NotifContext';
import { useRsrc } from '@/common/rsrc/RsrcContext';
import { Title } from '@/common/title/Title';
import { DateUtils } from '@/common/utils/DateUtils';
import { ExprUtils } from '@/common/utils/ExprUtils';
import { far } from '@awesome.me/kit-b4fd369ea4/icons';
import { Box } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useTab } from '../entityDetail/TabContext';
import { PivotColsButton } from './support/PivotColsButton';

export const PerspectiveTrxPivotViewer = () => {

  const [columns, setColumns] = useState();
  const [rows, setRows] = useState();
  const [values, setValues] = useState();
  const [actionJob, setActionJob] = useState();
  const [loadFieldDefs, setLoadFieldDefs] = useState(true);

  const [fieldDefs, setFieldDefs] = useState();
  const rsrc = useRsrc();
  const module = useModuleFromRoute();

  const gridRef = useRef();

  const [exportReq, setExportReq] = useState();

  const { notifs } = useNotif();

  // NavState
  const { navState, assign } = useNavState();
  const { tabId } = useTab();
  const pivotNavStateId = tabId ? tabId + 'PivotTableState' : 'pivotTableState'
  const pivotNavState = navState[pivotNavStateId] ?? {};

  // ----- NavState -----

  const saveNavState = () => {
    const newPerspectiveNavState = {
      columns,
      rows,
      value: values
    }

    assign(pivotNavStateId, newPerspectiveNavState);
  }

  useEffect(() => {
    saveNavState();
  }, [columns, rows, values]);

  // ----- Viewer -----

  const onGridReady = (params) => {
    const dataSource = {
      getRows: (params) => {

        const expr = ExprUtils.filterModelToExpr(params.filterModel, fieldDefs);

        const reqParam = {
          rows: rows ? rows.map(row => row.field) : [],
          value: values ? values.map(value => value.field) : [],
          entity: navState.entity,
          trxGroup: navState.trxGroup,
          startRow: params.startRow,
          endRow: params.endRow,
          expr
        };

        const plugin = 'PERSPECTIVE_TRX_PIVOT_VIEWER_PLUGIN';

        buildAndSetExportReq(reqParam, plugin);

        rsrc.viewerRows(module, plugin, reqParam, navState.entity).then(res => {
          gridRef.current.onGridSuccess(res, params);
          gridRef.current.resize();
        })

        rsrc.viewerCount(module, plugin, reqParam, navState.entity).then(count => {
          gridRef.current.setCount(count);
        })

        rsrc.viewerAggTotals(module, plugin, reqParam, navState.entity).then(valueTotals => {
          gridRef.current.setTotalsRow(valueTotals);
        })
      }
    }

    gridRef.current.setDataSource(dataSource);
  }

  useEffect(() => {

    if (!loadFieldDefs || !values || !rows) return;

    const reqParam = {
      rows: rows ? rows.map(row => row.field) : [],
      value: values ? values.map(value => value.field) : [],
      entity: navState.entity,
      trxGroup: navState.trxGroup
    };
    rsrc.viewerFieldDefs(module, 'PERSPECTIVE_TRX_PIVOT_VIEWER_PLUGIN', reqParam, navState.entity).then(fieldDefs => {
      setFieldDefs(fieldDefs);
    });

  }, [loadFieldDefs, values, rows]);

  // ----- Columns -----

  const buildAndSetExportReq = (reqParam, plugin) => {
    const exportReq = {
      timeZoneName: DateUtils.timeZoneName(),
      name: navState.title + ' Export',
      module,
      entity: navState.entity,
      uiType: 'VIEWER',
      viewerPluginCode: plugin,
      viewerReqParam: reqParam,
      fieldDefs,
    };

    setExportReq(exportReq);
  }

  useEffect(() => {

    // Restore state if applicable
    if (pivotNavState.columns) {
      setColumns(pivotNavState.columns);
      setRows(pivotNavState.rows);
      setValues(pivotNavState.value);
      setLoadFieldDefs(true);
      return;
    }

    const entity = navState.entity;
    const reqParam = {
      entity,
      trxGroup: navState.trxGroup,
      fetchReqParam: navState.fetchReqParam
    };

    rsrc.data(module, 'PERSPECTIVE_PIVOT_DATA_PLUGIN', reqParam, entity).then(cols => {
      setColumns(cols);
      setValues([]);
      setRows([]);
    });

  }, []);

  const setPivotCols = (newColumns, newRows, newValue) => {
    setColumns(newColumns);
    setRows(newRows);
    setValues(newValue);

    const actionReq = {
      rows: newRows.map(row => row.field),
      value: newValue.map(value => value.field),
      entity: navState.entity,
      fetchReqParam: navState.fetchReqParam,
      trxGroup: navState.trxGroup
    };

    const job = {
      name: 'Pivot on ' + navState.trxGroup,
      module,
      entity: navState.entity,
      rsrcCode: 'PERSPECTIVE_TRX_PIVOT_ACTION_PLUGIN',
      reqParamJson: JSON.stringify(actionReq),
      cat: 'ACTION'
    };

    rsrc.save('skdr', 'JOB', job).then(job => {
      setActionJob(job);
      setLoadFieldDefs(false);
      rsrc.start(job.id).then(run => { });
    });
  }

  useEffect(() => {
    if (!notifs || notifs.length === 0) return;

    notifs.forEach(notif => {
      const job = notif.event?.job;
      if (
        notif.eventType.startsWith('FINISH') &&
        job &&
        actionJob &&
        job.module === module &&
        job.entity === navState.entity &&
        actionJob.id === job.id
      ) {
        setLoadFieldDefs(true);
        setActionJob(null);
      }
    });
  }, [notifs]);

  return (
    fieldDefs &&
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%'
        }}
      >
        <Title icon={far.faTableColumns} title={'Pivot ' + navState.title} />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }} >
          <PivotColsButton columns={columns} rows={rows} value={values} setPivotCols={setPivotCols} />
          <ExportButton exportReq={exportReq} />
        </Box>
      </Box>
      {actionJob &&
        <Box sx={{ mb: 1 }} data-test-id="pivot-runner-banner">
          {<Banner key={actionJob.id} icon={far.faExclamationTriangle} text={'Running ' + actionJob.name} />}
        </Box>
      }
      <Grid
        key={JSON.stringify(fieldDefs)}
        ref={gridRef}
        gridOptions={{ fieldDefs }}
        events={{ onGridReady }}
      />
    </>
  )
}